import { useContext } from 'react';
import IconButton from '@mui/material/IconButton';

import { SimulationContext, type SubSimulationParams } from 'contexts/SimulationContext';
import { GeodataContext } from 'contexts/GeodataContext';

import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { Grid, TextInput } from 'components/lib';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { DeleteIcon } from 'components/icons';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';
import { getInputNumberRangeErrorMessage, isInputNumberInRange } from 'utils/inputNumberErrorMessage';
import '../styles/simulation.css';

const FreeView = () => {
  const { subSimulations, setSubSimulations, setSubSimulationParams, setSubSimulationName, defaultFreeview } =
    useContext(SimulationContext);
  const { geodataParams } = useContext(GeodataContext);
  const maxViewLength = Number(geodataParams?.lengthMid ?? 0) + Number(geodataParams?.lengthFar ?? 0);

  const resolvePreset = (params: SubSimulationParams = {}) => {
    const outward = Boolean(params?.outward);
    const angUp = Number(params?.angUp ?? 0);
    const angDown = Number(params?.angDown ?? 0);

    if (angUp === 0 && angDown === 0) {
      return outward ? 2 : 1;
    }

    return outward ? 4 : 3;
  };

  const getSimulationDescription = (params: SubSimulationParams = {}) => {
    const is180View = Boolean(params?.outward);

    return is180View ? (
      <>
        Use this simulation for analysis on an <strong>empty Grid</strong> or for <strong>large-scale land.</strong>
      </>
    ) : (
      <>
        Use this simulation for analysis around a <strong>Project Sketch.</strong>
      </>
    );
  };

  const toInputValue = (value: unknown): number | string | null => {
    if (value == null) return null;
    if (typeof value === 'number' || typeof value === 'string') return value;
    return null;
  };

  const handleInputsChange = (name: string | undefined, value: unknown, id: number) => {
    if (!name) return;
    setSubSimulationParams(id, { [name]: value });
  };

  const handleNameChange = (newName: string, id: number) => {
    setSubSimulationName(newName, id);
  };

  const addFreeviewSimulation = () => {
    const newId =
      subSimulations.length > 0 ? Math.max(...subSimulations.map((subSimulation) => subSimulation.id)) + 1 : 0;
    setSubSimulations([...subSimulations, defaultFreeview(newId)]);
  };

  const handleChangeSlider = (value: number | number[], id: number) => {
    const resolvedValue = Array.isArray(value) ? value[0] : value;
    handleInputsChange('raysResFactor', resolvedValue, id);
  };

  const removeBlock = (id: number) => {
    setSubSimulations(subSimulations.filter((block) => block.id !== id));
  };

  return (
    <>
      {subSimulations
        .filter((subSim) => subSim.simType === 'FREEVIEW')
        .map((subSimulation) => (
          <div key={subSimulation.id}>
            <br />
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                width: '100%',
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '24px',
                }}
              >
                <div style={{ width: '50%', minWidth: 0 }}>
                  <TextInput
                    label="Simulation Name"
                    name={`name`}
                    value={subSimulation?.name || ''}
                    onChange={(_name, value) => handleNameChange(value, subSimulation.id)}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: 'rgba(0, 0, 0, 0.65)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getSimulationDescription(subSimulation.params)}
                  </Typography>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '30px' }}>
                  <Typography variant="caption" style={{ flexShrink: 0 }}>
                    <span className="grid-inline-label-with-info">
                      <span>Simulation Accuracy</span>
                      <CustomTooltip
                        title={
                          'Defines the number of rays that will be used to discretize the visibility area or volume.'
                        }
                      />
                    </span>
                  </Typography>

                  <Slider
                    onChange={(_, value) => handleChangeSlider(value, subSimulation.id)}
                    value={Number(subSimulation.params?.raysResFactor ?? 50)}
                    min={10}
                    max={90}
                    step={10}
                    marks={[
                      {
                        value: 20,
                        label: 'Low',
                      },
                      {
                        value: 50,
                        label: 'Middle',
                      },
                      {
                        value: 80,
                        label: 'High',
                      },
                    ]}
                    sx={{
                      flex: 1,
                      mr: 1,
                    }}
                  />
                </div>
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                <IconButton size="large" color="error" onClick={() => removeBlock(subSimulation.id)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            </div>
            <br />

            <Grid cols="2">
              <Box sx={{ marginLeft: '2px' }}>
                <RadioGroup
                  value={resolvePreset(subSimulation.params)}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleNameChange(e.target.name, subSimulation.id);
                    switch (value) {
                      case 1:
                        handleInputsChange('angUp', 0, subSimulation.id);
                        handleInputsChange('angDown', 0, subSimulation.id);
                        handleInputsChange('outward', false, subSimulation.id);
                        break;
                      case 2:
                        handleInputsChange('angUp', 0, subSimulation.id);
                        handleInputsChange('angDown', 0, subSimulation.id);
                        handleInputsChange('outward', true, subSimulation.id);
                        break;
                      case 3:
                        handleInputsChange('angUp', 30, subSimulation.id);
                        handleInputsChange('angDown', 30, subSimulation.id);
                        handleInputsChange('outward', false, subSimulation.id);
                        break;
                      case 4:
                        handleInputsChange('angUp', 30, subSimulation.id);
                        handleInputsChange('angDown', 30, subSimulation.id);
                        handleInputsChange('outward', true, subSimulation.id);
                        break;
                      default:
                        break;
                    }
                  }}
                >
                  <FormControlLabel
                    value={1}
                    control={<Radio />}
                    name="Free View 360 2D"
                    className="free-view-option radio-row"
                    label={
                      <>
                        Free View 360 2D
                        <CustomTooltip
                          title={'The visibility area is a full horizontal circle around the sensor point.'}
                        />
                      </>
                    }
                  />
                  <FormControlLabel
                    value={2}
                    name="Free View 180 2D"
                    control={<Radio />}
                    className="free-view-option radio-row"
                    label={
                      <>
                        Free View 180 2D
                        <CustomTooltip
                          title={'The visibility area is a half-circle that points outwards from the site center.'}
                        />
                      </>
                    }
                  />
                  <FormControlLabel
                    value={3}
                    name="Free View 360 3D"
                    control={<Radio />}
                    className="free-view-option radio-row"
                    label={
                      <>
                        Free View 360 3D
                        <CustomTooltip
                          title={
                            'The visibiltiy volume is a sector of a sphere around the sensor point, defined by upwards and downwards angles.'
                          }
                        />
                      </>
                    }
                  />
                  <FormControlLabel
                    value={4}
                    name="Free View 180 3D"
                    control={<Radio />}
                    className="free-view-option radio-row"
                    label={
                      <>
                        Free View 180 3D
                        <CustomTooltip
                          title={
                            'The visibiltiy volume is a sector of a half-sphere that points outwards from the site center.'
                          }
                        />
                      </>
                    }
                  />
                </RadioGroup>
              </Box>
              <div style={{ paddingTop: '6px', maxWidth: '240px' }}>
                <UMInputNumber
                  min={0}
                  max={maxViewLength}
                  width={84}
                  maxErrorMessageWidth={276}
                  valid={isInputNumberInRange(subSimulation.params?.raysLength, 0, maxViewLength)}
                  errorMessage={getInputNumberRangeErrorMessage(0, maxViewLength)}
                  label={
                    <span className="grid-inline-label-with-info">
                      <span>View Length [m]</span>
                      <CustomTooltip
                        title={
                          'Can be defined according to the site characteristics and the goal of the analysis. Consider that the perception of space becomes inaccurate for distances over 100m'
                        }
                      />
                    </span>
                  }
                  name="raysLength"
                  value={toInputValue(subSimulation.params?.raysLength)}
                  onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                  defaultValue={100}
                />
              </div>
            </Grid>

            <Grid cols="6">
              {(resolvePreset(subSimulation.params) === 3 || resolvePreset(subSimulation.params) === 4) && (
                <UMInputNumber
                  label={
                    <span className="grid-inline-label-with-info">
                      <span>Angle up</span>
                      <CustomTooltip title={'Angle that defines the upper limit of the sphere sector'} />
                    </span>
                  }
                  name="angUp"
                  width={84}
                  maxErrorMessageWidth={276}
                  value={toInputValue(subSimulation.params?.angUp)}
                  onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                  defaultValue={30}
                />
              )}
              {(resolvePreset(subSimulation.params) === 3 || resolvePreset(subSimulation.params) === 4) && (
                <UMInputNumber
                  label={
                    <span className="grid-inline-label-with-info">
                      <span>Angle down</span>
                      <CustomTooltip title={'Angle that defines the lower limit of the sphere sector'} />
                    </span>
                  }
                  name="angDown"
                  width={84}
                  maxErrorMessageWidth={276}
                  value={toInputValue(subSimulation.params?.angDown)}
                  onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                  defaultValue={30}
                />
              )}
              <br />
              {/* <FormControlLabel
                disabled={false}
                control={
                  <Checkbox
                    name="outward"
                    checked={subSimulation.params?.outward}
                    onChange={(e) => {
                      handleInputsChange(e.target.name, e.target.checked, subSimulation.id);
                    }}
                  />
                }
                label="Outward"
              /> */}
            </Grid>

            <Divider />
            <br />
          </div>
        ))}
      <UMButton text="Add sim" onClick={addFreeviewSimulation} className="glass-btn-1" />
    </>
  );
};

export default FreeView;
